import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Calendar, RefreshCw, Clock, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import evidenceAPI from '../../services/evidenceAPI';
import type { EvidenceSource, CollectionFrequency } from '../../types/evidence';

interface ScheduleAutoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const FREQUENCY_OPTIONS: { value: CollectionFrequency; label: string; description: string }[] = [
  { value: 'on_demand',  label: 'On Demand',  description: 'Manual sync only'        },
  { value: 'daily',      label: 'Daily',      description: 'Every day at 2:00 AM'    },
  { value: 'weekly',     label: 'Weekly',     description: 'Every Monday at 2:00 AM' },
  { value: 'monthly',    label: 'Monthly',    description: '1st of each month'       },
  { value: 'quarterly',  label: 'Quarterly',  description: 'Every 3 months'          },
];

interface SourceSchedule {
  collection_frequency: CollectionFrequency;
  auto_sync_enabled: boolean;
  dirty: boolean;
  saving: boolean;
}

const ScheduleAutoCaptureModal: React.FC<ScheduleAutoCaptureModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [sources, setSources] = useState<EvidenceSource[]>([]);
  const [schedules, setSchedules] = useState<Record<number, SourceSchedule>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSources();
    }
  }, [isOpen]);

  const loadSources = async () => {
    setLoading(true);
    try {
      const data = await evidenceAPI.getEvidenceSources();
      const list = Array.isArray(data) ? data : (data as any).data ?? [];
      setSources(list);

      const initial: Record<number, SourceSchedule> = {};
      list.forEach((s: EvidenceSource) => {
        initial[s.id] = {
          collection_frequency: s.collection_frequency ?? 'daily',
          auto_sync_enabled: s.auto_sync_enabled,
          dirty: false,
          saving: false,
        };
      });
      setSchedules(initial);
    } catch {
      toast.error('Failed to load evidence sources');
    } finally {
      setLoading(false);
    }
  };

  const handleFrequencyChange = (sourceId: number, freq: CollectionFrequency) => {
    setSchedules(prev => ({
      ...prev,
      [sourceId]: { ...prev[sourceId], collection_frequency: freq, dirty: true }
    }));
  };

  const handleToggleAutoSync = (sourceId: number) => {
    setSchedules(prev => ({
      ...prev,
      [sourceId]: {
        ...prev[sourceId],
        auto_sync_enabled: !prev[sourceId].auto_sync_enabled,
        dirty: true
      }
    }));
  };

  const handleSaveSource = async (sourceId: number) => {
    const schedule = schedules[sourceId];
    if (!schedule) return;

    setSchedules(prev => ({ ...prev, [sourceId]: { ...prev[sourceId], saving: true } }));
    try {
      await evidenceAPI.updateEvidenceSource(sourceId, {
        collection_frequency: schedule.collection_frequency,
        auto_sync_enabled: schedule.auto_sync_enabled,
      });
      setSchedules(prev => ({
        ...prev,
        [sourceId]: { ...prev[sourceId], dirty: false, saving: false }
      }));
      toast.success('Schedule updated');
    } catch {
      toast.error('Failed to update schedule');
      setSchedules(prev => ({ ...prev, [sourceId]: { ...prev[sourceId], saving: false } }));
    }
  };

  const handleSaveAll = async () => {
    const dirtyIds = Object.entries(schedules)
      .filter(([, s]) => s.dirty)
      .map(([id]) => parseInt(id));

    if (dirtyIds.length === 0) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }

    await Promise.all(dirtyIds.map(id => handleSaveSource(id)));
    onSuccess();
  };

  const sourceTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      github: '🐙',
      ssl_labs: '🔒',
      aws: '☁️',
      siem_elastic: '📊',
      siem_splunk: '📡',
      manual: '📁',
    };
    return icons[type] ?? '🔗';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-purple-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Schedule Auto-Capture</h2>
              <p className="text-xs text-gray-500">Configure automatic sync schedules for each evidence source</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No evidence sources configured</p>
              <p className="text-sm mt-1">Add evidence sources first to schedule auto-capture.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sources.map(source => {
                const sched = schedules[source.id];
                if (!sched) return null;
                return (
                  <div
                    key={source.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      sched.dirty ? 'border-purple-300 bg-purple-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{sourceTypeIcon(source.source_type)}</span>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{source.source_name}</p>
                          <p className="text-xs text-gray-500 capitalize">{source.source_type.replace('_', ' ')}</p>
                        </div>
                      </div>

                      {/* Auto-sync toggle */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600">Auto-sync</span>
                        <button
                          type="button"
                          onClick={() => handleToggleAutoSync(source.id)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            sched.auto_sync_enabled ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow ${
                              sched.auto_sync_enabled ? 'translate-x-4' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Frequency selector */}
                    <div className={`transition-opacity ${sched.auto_sync_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                      <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Frequency
                      </p>
                      <div className="grid grid-cols-5 gap-1.5">
                        {FREQUENCY_OPTIONS.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleFrequencyChange(source.id, opt.value)}
                            title={opt.description}
                            className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                              sched.collection_frequency === opt.value
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {sched.auto_sync_enabled && (
                        <p className="text-xs text-gray-400 mt-1.5">
                          {FREQUENCY_OPTIONS.find(o => o.value === sched.collection_frequency)?.description}
                        </p>
                      )}
                    </div>

                    {/* Next collection + last sync dates */}
                    {!sched.dirty && (
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded px-3 py-2">
                          <p className="text-xs text-gray-400 mb-0.5">Next Collection</p>
                          <p className="text-xs font-medium text-gray-700">
                            {sched.auto_sync_enabled && source.next_scheduled_sync
                              ? new Date(source.next_scheduled_sync).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : sched.auto_sync_enabled
                              ? 'Scheduled (pending save)'
                              : '—  Auto-sync off'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded px-3 py-2">
                          <p className="text-xs text-gray-400 mb-0.5">Last Sync</p>
                          <p className="text-xs font-medium text-gray-700">
                            {source.last_sync_date
                              ? new Date(source.last_sync_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Save individual */}
                    {sched.dirty && (
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleSaveSource(source.id)}
                          disabled={sched.saving}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                        >
                          {sched.saving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <CheckCircle className="h-3 w-3" />
                          )}
                          Save
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            Changes marked in purple are unsaved
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm transition-colors"
            >
              Save All Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAutoCaptureModal;
